import type { Idl, Program } from '@coral-xyz/anchor';
import type { AccountInfo, Connection, PublicKey } from '@solana/web3.js';
import { MORFI_OPINION_PROGRAM_ID } from '../constants/programs.js';
import { getProgram } from '../idl/loader.js';
import { deriveMarketPda } from '../pda/derivations.js';
import { fetchStagedOutcomeMetadata, type StagedOutcomeMetadata } from './outcome-metadata.js';
import { IncompatiblePreparationAccountError } from './preparation-errors.js';
import type { MarketAccount } from './types.js';

export type { StagedOutcomeMetadata } from './outcome-metadata.js';

export interface PreparationMarketSnapshot {
  address: PublicKey;
  slot: number;
  account: MarketAccount | null;
  incompatibleReason?: string;
}

export { IncompatiblePreparationAccountError } from './preparation-errors.js';

export async function fetchPreparationMarkets(
  connection: Connection,
  addresses: PublicKey[],
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
  minContextSlot: number = 0,
  commitment: 'confirmed' | 'finalized' = 'confirmed',
): Promise<PreparationMarketSnapshot[]> {
  if (addresses.length === 0 || addresses.length > 100) {
    throw new Error('Preparation market reads require 1..100 addresses');
  }
  const program = await getProgram(connection, programId);
  const snapshot = await connection.getMultipleAccountsInfoAndContext(addresses, {
    commitment,
    minContextSlot,
  });
  if (snapshot.context.slot < minContextSlot) throw new Error('Preparation RPC context is behind');
  return addresses.map((address, index) => {
    const info = snapshot.value[index];
    try {
      return {
        address,
        slot: snapshot.context.slot,
        account: info == null ? null : decodeMarket(program, address, info),
      };
    } catch (error) {
      if (!(error instanceof IncompatiblePreparationAccountError)) throw error;
      return {
        address,
        slot: snapshot.context.slot,
        account: null,
        incompatibleReason: error.message,
      };
    }
  });
}

export async function fetchPreparationMetadata(
  connection: Connection,
  market: PublicKey,
  account: MarketAccount,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
  minContextSlot: number = 0,
): Promise<StagedOutcomeMetadata> {
  const metadata = await fetchStagedOutcomeMetadata(
    connection,
    {
      market,
      creator: account.creator,
      marketNonce: account.marketNonce,
    },
    programId,
    minContextSlot,
  );
  if (metadata === null)
    throw new IncompatiblePreparationAccountError('Staged outcome metadata is absent');
  if (metadata.initialOutcomes > account.nOutcomes) {
    throw new IncompatiblePreparationAccountError(
      'Staged metadata identity or active prefix differs from Market',
    );
  }
  // An append can land between the Market and metadata snapshots; retry the
  // Market read instead of permanently excluding a valid evolving market.
  if (metadata.outcomes.length !== account.nOutcomes)
    throw new Error('Staged metadata active prefix changed during recovery');
  return metadata;
}

function decodeMarket(
  program: Program<Idl>,
  address: PublicKey,
  info: AccountInfo<Buffer>,
): MarketAccount {
  if (!info.owner.equals(program.programId)) {
    throw new IncompatiblePreparationAccountError('Preparation Market has an unexpected owner');
  }
  let account: MarketAccount;
  try {
    account = program.coder.accounts.decode<MarketAccount>('market', info.data);
  } catch {
    throw new IncompatiblePreparationAccountError('Preparation Market has an incompatible layout');
  }
  if (
    account.layoutVersion !== 17 ||
    !deriveMarketPda(account.creator, account.marketNonce, program.programId)[0].equals(address) ||
    !account.permanent ||
    !Number.isInteger(account.nOutcomes) ||
    account.nOutcomes < 2 ||
    account.nOutcomes > 10 ||
    (account.preparedOutcomesBitmap & ~((1 << account.nOutcomes) - 1)) !== 0
  ) {
    throw new IncompatiblePreparationAccountError('Preparation Market active prefix is invalid');
  }
  return account;
}
